import { useMemo, useState, useCallback } from 'react';
import { api } from '../../../core/api/queries';
import { useReportPage } from '../../../shared/hooks';
import { ReportPageLayout } from '../../../shared/components/layouts';
import { EditionSelector, renderSchoolName } from '../../../shared/components/tables';
import { columnBuilder } from '../../../shared/utils';
import type { EditionReportDto, SchoolReportDto } from '../api/schools.api';
import { schoolsApi } from '../api/schools.api';
import { SchoolDataPreviewDialog } from '../components/SchoolDataPreviewDialog';
import { useAuthProviderV2, useRoutesProvider } from '../../../providers/provider-hooks';

/**
 * Edition Status Report Page
 *
 * Complete overview of all schools' performance in a specific edition.
 * Includes waste collection, activity points, and contact details.
 *
 * **Features**:
 * - 50 columns organized by category
 * - Edition selector (auto-selects current edition)
 * - Full filtering, sorting, pagination
 * - Excel export with all columns
 *
 * **Architecture** (Refactored 2025-11-01):
 * - Uses useReportPage hook with withEditions=true (consolidates all state management)
 * - Uses ReportPageLayout component (consolidates JSX structure)
 * - Uses columnBuilder for column definitions
 * - Reduced from 212 lines to ~95 lines (55% reduction)
 *
 * **Default Home Page** for ADMIN and DBEST users
 */
export function EditionStatusReportPage() {
    // ==========================================
    // 1. DATA FETCHING & REPORT INTERFACE
    // ==========================================

    const { report, adapters, handleExport, editions, currentEdition, changeEdition } = useReportPage({
        queryHook: api.schools.statusReport,
        initialSort: [],
        exportEndpoint: '/api/schools/status-report',
        exportFilename: 'raport_status_editie',
        withEditions: true  // Auto-filters by selected edition
    });

    // ==========================================
    // 2. PREVIEW DIALOG STATE & HANDLERS
    // ==========================================

    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewData, setPreviewData] = useState<SchoolReportDto | null>(null);
    const [loadingPreviewData, setLoadingPreviewData] = useState(false);

    // Role-based visibility for preview button
    const { userInfo } = useAuthProviderV2();
    const { ROLES } = useRoutesProvider();
    const canViewPreviews = userInfo?.role && [ROLES.ADMIN, ROLES.DBEST].includes(userInfo.role);

    // Fetch school profile data for preview dialog
    const openPreviewDialog = useCallback(async (schoolId: string) => {
        setLoadingPreviewData(true);
        try {
            const schoolProfile = await schoolsApi.getSchoolProfile(schoolId);
            setPreviewData(schoolProfile);
            setPreviewDialogOpen(true);
        } catch (error: unknown) {
            console.error('Error loading school profile data:', error);
            alert('Eroare la încărcarea datelor. Vă rugăm încercați din nou.');
        } finally {
            setLoadingPreviewData(false);
        }
    }, []);

    // ==========================================
    // 3. COLUMN DEFINITIONS
    // ==========================================

    // Column configuration - 50 columns organized by category
    const columns = useMemo(() =>
        columnBuilder<EditionReportDto>()
            // ===== PREVIEW COLUMN (ADMIN/DBEST only) - FIRST COLUMN =====
            .when(!!canViewPreviews, (b) => b.custom({
                key: 'preview',
                label: 'Protocol',
                sortable: false,
                filterable: false,
                render: (_, row) => {
                    // 2026-10-20: starea e pe ediția rândului. ALTA_EDITIE = protocolul există, dar ultima
                    // încărcare e din alt an — se poate deschide, dar nu contează ca „încărcat” aici.
                    const stare = row.stareProtocol ?? (row.hasProtocol ? 'EDITIA_RAPORTULUI' : 'NEINCARCAT');
                    if (stare === 'NEINCARCAT') {
                        return <span className="text-red-600 text-xs font-semibold">NEÎNCĂRCAT</span>;
                    }

                    const afiseaza = (
                        <button
                            disabled={report.loading || loadingPreviewData || !row.id}
                            onClick={() => row.id && openPreviewDialog(row.id)}
                            className="text-nowrap hover:underline focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline font-bold"
                        >
                            Afișează
                        </button>
                    );
                    if (stare !== 'ALTA_EDITIE') return afiseaza;

                    return (
                        <span className="flex flex-col items-start">
                            <span className="text-warning-text text-xs font-semibold text-nowrap">ALTĂ EDIȚIE</span>
                            {afiseaza}
                        </span>
                    );
                },
            }))

            // ===== SCHOOL IDENTITY (4 columns) =====
            // Ramane vizibila la derularea celor 50 de coloane. E marcata pe NUME,
            // nu pe pozitie: coloana `preview` de mai sus e conditionata de rol,
            // deci „prima coloana" nu e aceeasi pentru toti.
            .text('numeInstitutieInvatamant', 'Denumire instituție învățământ', 'Denumire instituție...', {
                sticky: true,
                // Normalizare doar la afisare; datele si exportul raman neatinse.
                render: renderSchoolName,
                cardRole: 'title',
            })
            .text('codSe', 'Cod SE', 'Cod SE...')
            .text('cui', 'C.U.I.', 'C.U.I....')
            .custom({
                key: 'editionId',
                label: 'Ediția',
                sortable: false,
                filterable: false,
                render: () => currentEdition?.nume || '-',
            })

            // ===== WASTE COLLECTION (4 columns) =====
            .number('totalDeeeKg', 'Kg DEEE', 'Kg DEEE...')
            .number('totalDbaKg', 'Kg DBA', 'Kg DBA...')
            .number('totalBecuriSiNeoaneKg', 'Kg Becuri și Neoane', 'Kg Becuri și Neoane...')
            // Cifra care insoteste numele scolii in rezumatul de pe mobil.
            .number('totalKg', 'Total kg', 'Total kg...', { cardRole: 'value' })

            // ===== ACTIVITY POINTS (4 columns) =====
            .number('pointsPracticalActivity', 'Total puncte din activități practice', 'Total puncte...')
            .number('pointsHomework', 'Total puncte din teme de casă', 'Total puncte...')
            .number('pointsDailyChallenge', 'Total puncte din provocări zilnice', 'Total puncte...')
            .number('pointsMonthlyChallenge', 'Total puncte din provocări lunare', 'Total puncte...')
            .number('pointsSchoolChallenge', 'Total puncte din provocări pentru școli', 'Total puncte...')

            // ===== ACTIVITY COUNTS (4 columns) =====
            .number('countPracticalActivity', 'Activități practice aprobate', 'Activități practice...')
            .number('countHomework', 'Teme de casă aprobate', 'Teme de casă...')
            .number('countDailyChallenge', 'Provocări zilnice aprobate', 'Provocări zilnice...')
            .number('countMonthlyChallenge', 'Provocări lunare aprobate', 'Provocări lunare...')
            .number('countSchoolChallenge', 'Provocări pentru școli aprobate', 'Provocări pentru școli...')

            // ===== LESSON & ORDER POINTS (3 columns) =====
            .number('pointsAllLessonsTotal', 'Total puncte din lecții', 'Total puncte...')
            .number('pointsWasteCollectionTotal', 'Puncte din comenzi de colectare', 'Puncte comenzi...')
            .number('pointsGrandTotal', 'Total puncte', 'Total puncte...')

            // ===== MAIN ADDRESS (11 columns) =====
            .text('mainJudet', 'Județ', 'Județ...')
            .text('mainOras', 'Oraș', 'Oraș...')
            .text('mainComuna', 'Comună', 'Comună...')
            .text('mainSat', 'Sat', 'Sat...')
            .text('mainStrada', 'Stradă', 'Stradă...')
            .text('mainNumar', 'Număr', 'Număr...')
            .text('mainCodPostal', 'Cod poștal', 'Cod poștal...')
            .text('mainBloc', 'Bloc', 'Bloc...')
            .text('mainScara', 'Scară', 'Scară...')
            .text('mainEtaj', 'Etaj', 'Etaj...')
            .text('mainApartament', 'Apartament', 'Apartament...')

            // ===== DELIVERY ADDRESS (11 columns) =====
            .text('deliveryJudet', 'Județ livrare', 'Județ livrare...')
            .text('deliveryOras', 'Oraș livrare', 'Oraș livrare...')
            .text('deliveryComuna', 'Comună livrare', 'Comună livrare...')
            .text('deliverySat', 'Sat livrare', 'Sat livrare...')
            .text('deliveryStrada', 'Stradă livrare', 'Stradă livrare...')
            .text('deliveryNumar', 'Număr livrare', 'Număr livrare...')
            .text('deliveryCodPostal', 'Cod poștal livrare', 'Cod poștal livrare...')
            .text('deliveryBloc', 'Bloc livrare', 'Bloc livrare...')
            .text('deliveryScara', 'Scară livrare', 'Scară livrare...')
            .text('deliveryEtaj', 'Etaj livrare', 'Etaj livrare...')
            .text('deliveryApartament', 'Apartament livrare', 'Apartament livrare...')

            // ===== CONTACT & PROFESSOR (6 columns) =====
            .text('telefon', 'Telefon', 'Telefon...')
            .text('numeReprezentatLegal', 'Nume reprezentant legal', 'Nume reprezentant...')
            .text('professorNume', 'Nume profesor', 'Nume profesor...')
            .text('professorEmail', 'Email profesor', 'Email profesor...')
            .text('professorTelefon', 'Telefon profesor', 'Telefon profesor...')
            .number('numarElevi', 'Număr elevi', 'Număr elevi...')

            // ===== BOOLEAN FLAGS (3 columns) =====
            .boolean('detineRecipiente', 'Deține recipiente')
            .boolean('participareEditieAnterioara', 'Participare ediție anterioară')
            .boolean('adresaLivrareAceeasiCuScoala', 'Adresă de livrare aceeași cu școala')

            .build(),
        [currentEdition, canViewPreviews, report.loading, loadingPreviewData, openPreviewDialog]
    );

    // ==========================================
    // 4. RENDER
    // ==========================================

    return (
        <>
            <ReportPageLayout
                title="Raport Ediție"
                report={report}
                adapters={adapters}
                handleExport={handleExport}
                columns={columns}
                entityLabel="scoli"
                unitLabel="kg"
                extraControls={
                    <EditionSelector
                        editions={editions || []}
                        currentEditionId={currentEdition?.id}
                        onChange={changeEdition || (() => {})}
                        disabled={report.loading}
                    />
                }
            />

            {/* School Preview Dialog */}
            <SchoolDataPreviewDialog
                isOpen={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                data={previewData}
                isLoading={loadingPreviewData}
            />
        </>
    );
}
